<template>
  <div
    :class="showSticky ? 'sticky top-0 z-[410]' : 'relative'"
    class="flex justify-center"
  >
    <header
      class="header transition-all"
      :class="[{ 'is-open': isOpen }, setStickyStyling ? 'header-small' : 'lg:h-28']"
    >
      <div
        class="hidden lg:block w-full h-10 absolute top-0 right-0 bg-gradient-to-l from-gray-50 to-transparent z-[-1]"
        :class="setStickyStyling ? 'opacity-0' : 'opacity-100'"
      />
      <div class="header-inner">
        <div class="header-logo">
          <div
            :class="setStickyStyling ? 'lg:top-0' : 'lg:top-2'"
            class="header-logo-primary"
          >
            <a
              v-if="!headerDisableLogoLink"
              :href="`${headerLogoLink}`"
              :aria-label="
                i18n && i18n.backToHomepage ? i18n.backToHomepage : 'Back to the homepage'
              "
            >
              <kf-logo
                :enable-anniversary-logo="enableAnniversaryLogo"
                :logo="headerLogo"
                :set-sticky-styling="setStickyStyling"
                :logo-text="i18n.logoTitle"
                :locale="headerLocale"
              />
            </a>
            <kf-logo
              v-if="headerDisableLogoLink"
              :enable-anniversary-logo="enableAnniversaryLogo"
              :logo="headerLogo"
              :locale="headerLocale"
              :set-sticky-styling="setStickyStyling"
              :logo-text="i18n.logoTitle"
            />
          </div>
          <div
            v-if="headerLogoSecondaryLogoUrl"
            class="header-logo-secondary"
          >
            <img
              :src="headerLogoSecondaryLogoUrl"
              :alt="headerLogoSecondaryLogoAlt"
              class="header__secondary-logo"
            >
          </div>
        </div>
        <div
          class="header-top transition-opacity"
          :class="[
            { 'has-secondary-logo': headerLogoSecondaryLogoUrl },
            setStickyStyling ? 'opacity-0' : 'opacity-100',
          ]"
        >
          <kf-navigation-meta
            :nav-meta-items="headerNavMetaItems"
            :nav-meta-current-locale="headerLocale"
            :nav-meta-languages="headerLanguages"
          />
        </div>
        <div
          class="header-bottom"
          :class="{ 'has-secondary-logo': headerLogoSecondaryLogoUrl }"
        >
          <kf-navigation-main
            ref="mainNav"
            :nav-main-items="headerNavMainItems"
            :class="[isOpen ? 'is-open' : 'is-closed']"
            :nav-main-search-action="headerNavMainSearchAction"
            :nav-main-search-placeholder="headerNavMainSearchPlaceholder"
            :nav-main-cta="headerNavMainCta"
            :nav-sub-state="subnavIsOpen"
            :set-sticky-styling="setStickyStyling"
            :use-compact-navigation="useCompactNavigation"
            @clicked="onClickChild"
            @focusSubmenu="handleFocusSubmenu"
            @closeSub="closeSubmenu"
          >
            <kf-navigation-meta
              class="only-small bg-gray-50 pb-4"
              :nav-meta-items="headerNavMetaItems"
              :nav-meta-current-locale="headerLocale"
              :nav-meta-languages="headerLanguages"
            />
          </kf-navigation-main>
        </div>

        <kf-link
          v-if="headerNavMainCta"
          class="header-cta hidden lg:visible"
          type="button-secondary"
          :href="headerNavMainCta.href"
        >
          {{ headerNavMainCta.label }}
        </kf-link>
        <div class="header-menu-btn lg:hidden">
          <template v-if="headerNavMainCta && headerNavMainCta.length <= 1">
            <kf-link
              v-for="(link, index) in headerNavMainCta"
              :key="`${link.label}-${index}`"
              :href="link.href"
              type="button-secondary"
              class="h-12 text-base w-fit absolute top-[6px] right-28 lg:hidden"
              :class="{
                'first-button red': index === 0,
              }"
            >
              <span class="flex justify-center items-center gap-2">
                <img
                  v-if="link.icon"
                  :src="link.icon"
                  :alt="link.label"
                  class="h-4 w-4"
                ><span>{{
                  link.label
                }}</span>
              </span>
            </kf-link>
          </template>
          <kf-button
            class="btn-nav"
            @click="toggleMenu"
          >
            <div>
              <span class="absolute right-7 bottom-0">Menu</span>
              <div class="block">
                <span
                  aria-hidden="true"
                  class="btn-nav__line btn-nav__line--first"
                /><span
                  aria-hidden="true"
                  class="btn-nav__line btn-nav__line--second"
                /><span
                  aria-hidden="true"
                  class="btn-nav__line btn-nav__line--third"
                />
              </div>
            </div>
            <span class="sr-only">{{ i18n && i18n.openMenu ? i18n.openMenu : 'Open menu' }}</span>
          </kf-button>
        </div>
      </div>
      <kf-navigation-sub
        v-if="!useCompactNavigation"
        ref="subNav"
        :nav-sub-items="navSubItems"
        :nav-sub-extra-items="headerNavSubExtraItems"
        :class="{ 'is-open': subnavIsOpen }"
        @closeSub="closeSubmenu"
        @focusPreviousElement="focusLastActiveMainItem"
        @focusNextElement="focusNextMainMenuItem"
      />
    </header>
  </div>
</template>

<script>
import KfButton from '@/components/base/button/Button.vue';
import KfLink from '@/components/base/link/Link.vue';
import KfLogo from '@/components/base/logo/Logo.vue';
import KfNavigationMain from '@/components/navigation/navigation-main/NavigationMain.vue';
import KfNavigationMeta from '@/components/navigation/navigation-meta/NavigationMeta.vue';
import KfNavigationSub from '@/components/navigation/navigation-sub/NavigationSub.vue';

export default {
  name: 'KfHeader',
  components: {
    KfLogo,
    KfNavigationMain,
    KfNavigationMeta,
    KfNavigationSub,
    KfButton,
    KfLink,
  },
  props: {
    enableAnniversaryLogo: {
      type: Boolean,
      default: false,
    },
    headerLogo: {
      type: String,
      default: '',
    },
    headerDisableLogoLink: {
      type: Boolean,
      default: false,
    },
    headerLogoSecondaryLogoUrl: {
      type: String,
      default: '',
    },
    headerLogoSecondaryLogoAlt: {
      type: String,
      default: '',
    },
    headerNavMainItems: {
      type: Array,
      default: null,
    },
    headerNavMainSearchAction: {
      type: String,
      default: '',
    },
    headerNavMainSearchPlaceholder: {
      type: String,
      default: '',
    },
    headerNavMetaItems: {
      type: Array,
      default: null,
    },
    headerLocale: {
      type: String,
      default: 'nl',
    },
    headerLogoLink: {
      type: String,
      default: '/',
    },
    headerLanguages: {
      type: Array,
      default: null,
    },
    headerNavMainCta: {
      type: Array,
      default: null,
    },
    headerNavSubExtraItems: {
      type: Array,
      default: null,
    },
    showSticky: {
      type: Boolean,
      default: false,
    },
    i18n: {
      type: Object,
      default: null,
    },
    useCompactNavigation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      subnavIsOpen: false,
      setStickyStyling: false,
      selectedItem: {},
      lastActiveMainItem: null,
      nextMainMenuItem: null,
      lastActiveButtonId: null,
      nextButtonId: null,
    };
  },
  computed: {
    navSubItems() {
      return this.selectedItem?.navigationSub || [];
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);

    // Add a global keydown listener to handle tab events
    document.addEventListener('keydown', this.handleGlobalTabKey);
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('keydown', this.handleGlobalTabKey);
  },
  methods: {
    handleGlobalTabKey(event) {
      if (this.subnavIsOpen && event.key === 'Tab') {
        const focusableSelector =
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
        const submenu = document.querySelector('.sub-navigation.is-open');

        if (!submenu) return;

        // Get all focusable elements in the submenu
        const submenuFocusables = Array.from(submenu.querySelectorAll(focusableSelector));
        if (submenuFocusables.length === 0) return;

        // Get all main menu buttons
        const mainNavButtons = Array.from(
          document.querySelectorAll('.main-navigation-list-link.is-toggle'),
        );

        // Find current active button
        const activeButtonIndex = mainNavButtons.findIndex(
          (button) => button.textContent.trim() === this.selectedItem.Name,
        );

        if (activeButtonIndex === -1) return;

        // Get the next button after the active one
        const nextButton = mainNavButtons[activeButtonIndex + 1];
        if (!nextButton) return;

        const focusedElement = document.activeElement;
        if (submenu.contains(focusedElement)) {
          // If we're on the last element and tabbing forward
          if (
            focusedElement === submenuFocusables[submenuFocusables.length - 1] &&
            !event.shiftKey
          ) {
            event.preventDefault();
            this.closeSubmenu();
            setTimeout(() => {
              nextButton.focus();
            }, 50);
          }
        }
      }
    },
    handleScroll() {
      if (window.scrollY > 209 && this.showSticky) {
        this.setStickyStyling = true;
      } else if (window.scrollY < 140) {
        this.setStickyStyling = false;
      }
    },
    changeLanguage(headerLocale) {
      this.$formulate.setLocale(headerLocale);
    },
    toggleMenu: function () {
      // flip the mobile menu
      this.isOpen = !this.isOpen;

      // always clear any open submenu when closing
      if (!this.isOpen) {
        this.closeSubmenu();
      }

      // scroll‑lock on mobile (below lg)
      if (window.innerWidth < 1024) {
        document.body.style.overflow = this.isOpen ? 'hidden' : '';
      }
    },
    onClickChild(selectedItem) {
      if (window.innerWidth < 1024) {
        if (this.selectedItem === selectedItem) {
          this.subnavIsOpen = false;
          this.selectedItem = {};
        } else {
          this.selectedItem = selectedItem;
          this.subnavIsOpen = true;
        }
      }
      // Desktop handling with keyboard accessibility
      else {
        if (this.$refs.subNav) {
          this.$refs.subNav.mouseClickDetected = true;
        }

        // Store the currently clicked item for focus management
        const buttons = document.querySelectorAll('.main-navigation-list-link.is-toggle');
        for (let i = 0; i < buttons.length; i++) {
          const button = buttons[i];
          if (button.textContent.trim() === selectedItem.Name) {
            this.lastActiveMainItem = button;
            break;
          }
        }

        const isTogglingOpen =
          this.subnavIsOpen && this.selectedItem && this.selectedItem.Name === selectedItem.Name;

        if (isTogglingOpen) {
          this.subnavIsOpen = false;
          this.selectedItem = {};
        } else {
          this.selectedItem = selectedItem;
          this.subnavIsOpen = true;

          // Only auto-focus if triggered by keyboard
          if (document.activeElement === this.lastActiveMainItem) {
            this.$nextTick(() => {
              if (this.$refs.subNav) {
                this.$refs.subNav.focusFirstItem();
              }
            });
          }
        }
      }
    },
    handleFocusSubmenu(selectedItem) {
      // Store the currently active main menu item for focus management
      const buttons = document.querySelectorAll('.main-navigation-list-link.is-toggle');

      let activeButtonIndex = -1;

      for (let i = 0; i < buttons.length; i++) {
        const button = buttons[i];

        if (button.textContent.trim() === selectedItem.Name) {
          this.lastActiveMainItem = button;
          activeButtonIndex = i;
          this.lastActiveButtonId = button.id;
          break;
        }
      }

      // Store the next main menu item's ID for when tabbing out of submenu
      if (activeButtonIndex !== -1 && activeButtonIndex < buttons.length - 1) {
        const nextButton = buttons[activeButtonIndex + 1];
        this.nextButtonId = nextButton.id;
      } else {
        this.nextButtonId = null;
      }

      // Always update selected item and open submenu
      this.selectedItem = selectedItem;
      this.subnavIsOpen = true;

      // Use nextTick to ensure the submenu is rendered before focusing
      this.$nextTick(() => {
        if (this.$refs.subNav) {
          this.$refs.subNav.focusFirstItem();
        }
      });
    },
    beforeDestroy() {
      document.body.style.overflow = '';
    },
    closeSubmenu() {
      this.subnavIsOpen = false;
      this.selectedItem = {};
    },
    focusLastActiveMainItem() {
      // Return focus to the last active main menu item
      if (this.lastActiveMainItem) {
        this.$nextTick(() => {
          this.lastActiveMainItem.focus();
        });
      }
    },
    focusNextMainMenuItem() {
      if (this.nextButtonId) {
        const nextButton = document.getElementById(this.nextButtonId);

        if (nextButton) {
          setTimeout(() => {
            try {
              nextButton.focus();
            } catch (error) {
              console.error('Error focusing button by ID:', error);
            }
          }, 50);
        }
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.is-button-primary.pointer-events-auto {
  @apply py-2;
}
.header {
  @apply w-full flex bg-white z-30 shadow;
  min-height: 60px;

  .first-button {
    @apply border-gray-75 border text-gray-500;

    &.red {
      @apply border-primary-bravo text-primary-bravo hover:text-white;
    }
  }

  @screen lg {
    &-small {
      height: 68px;
    }
    @apply px-0;
  }
  &-menu-btn {
    .btn-nav {
      @apply absolute block mr-4;
      right: 0;
      top: 20px;
      height: 20px;
      width: 20px;

      @screen lg {
        top: 50px;
      }

      @screen xl {
        @apply hidden;
      }

      &__line {
        display: block;
        width: 18px;
        height: 2px;
        border-radius: 2px;
        background-color: theme('colors.primary.bravo');
        position: absolute;
        left: 0;
        transition: all 300ms;
      }

      &__line--first {
        top: 3px;
      }

      &__line--second {
        top: 9px;
      }

      &__line--third {
        top: 15px;
        width: 13px;
      }
    }
  }

  &-logo {
    @apply absolute flex items-center;
    top: 0;
    left: 0;
    z-index: 11;

    @screen lg {
      @apply top-0 left-0;
    }

    &-primary {
      @apply relative;
    }

    &-secondary {
      @apply hidden h-2/4 z-20 ml-4;

      @screen sm {
        @apply flex flex-col flex-nowrap justify-center items-start flex-grow ml-0;
      }

      @screen lg {
        @apply flex-grow-0 mr-10;
        transform: translateY(10px);
      }

      img {
        max-width: 8rem;
        max-height: 100%;
      }
    }
  }

  &.is-open {
    @screen md {
      @apply bg-transparent;
    }

    .btn-primary {
    }

    .btn-nav {
      &__line--first {
        top: 50%;
        transform: rotate(-45deg);
      }

      &__line--second {
        left: 10px;
        opacity: 0;
      }

      &__line--third {
        top: 50%;
        transform: rotate(45deg);
        width: 18px;
      }
    }
  }

  &-inner {
    @apply relative w-full mx-auto flex flex-col justify-end lg:container px-5 md:px-4;

    &-right {
      @screen lg {
        @apply flex-col items-end justify-center w-auto relative;
      }
    }
  }

  &-top {
    @apply flex justify-end ml-36 mb-auto;
    height: 40px;

    .navigation-meta {
      @apply hidden;

      @screen lg {
        @apply flex;
      }
    }

    &.has-secondary-logo {
      @apply ml-80;
    }
  }

  &-bottom {
    @apply flex ml-36;

    &.has-secondary-logo {
      @apply ml-80;
    }
  }

  &-cta {
    @apply absolute py-2 px-4 hidden;
    z-index: 10;
    top: 8px;
    right: 10px;

    @screen md {
      @apply hidden;
    }
  }
}
</style>
